import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import withRoleAccess from '../../hoc/withRoleAccess';
import axios from 'axios';
import './TeacherHours.css';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Card,
  Table,
  Typography,
  Select,
  Button,
  Input,
  Tag,
  Modal,
  Checkbox,
  Space,
  InputNumber,
  message,
  Tooltip,
  Descriptions,
  Alert
} from 'antd';
import {
  SearchOutlined,
  FileExcelOutlined,
  HomeOutlined,
  VideoCameraOutlined,
  EnvironmentOutlined,
  UserOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) {
    return 'Invalid Date';
  }
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  return new Intl.DateTimeFormat('en-GB', options).format(date).toUpperCase();
};

const TeacherDetailsPage = () => {
  const { teacherId } = useParams();
  const { t } = useTranslation();
  const [teacher, setTeacher] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [tempMonth, setTempMonth] = useState(new Date().getMonth() + 1);
  const [tempYear, setTempYear] = useState(new Date().getFullYear());
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [hourlyRates, setHourlyRates] = useState({});
  const [isApproved, setIsApproved] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [withholdingTax, setWithholdingTax] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState({});
  const [registrationCounts, setRegistrationCounts] = useState({});
  const [teacherRate, setTeacherRate] = useState(null);
  const [socialSecurityDeduction, setSocialSecurityDeduction] = useState(0);
  const [otherDeduction, setOtherDeduction] = useState(0);
  const [otherDeductionNote, setOtherDeductionNote] = useState('');
  const [otherAddition, setOtherAddition] = useState(0);
  const [otherAdditionNote, setOtherAdditionNote] = useState('');
  const [remarks, setRemarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [showOnlyUnpaid, setShowOnlyUnpaid] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState([]);
  
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches(res.data);
    } catch (err) {
      console.error("❌ Failed to fetch branches", err);
    }
  };

  fetchBranches();
}, [API_URL]);


  const fetchTeacher = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/teachers/${teacherId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setTeacher(response.data);
    } catch (error) {
      console.error(t('errorFetchingTeacher'), error);
      message.error(t('errorFetchingTeacher'));
    } finally {
      setLoading(false);
    }
  }, [teacherId, API_URL, t]);

  const fetchClasses = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/classes/teacher/${teacherId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setClasses(response.data);
      filterClasses(response.data, selectedMonth, selectedYear);
    } catch (error) {
      console.error(t('errorFetchingClasses'), error);
      message.error(t('errorFetchingClasses'));
    } finally {
      setLoading(false);
    }
  }, [teacherId, selectedMonth, selectedYear, API_URL, t]);

const fetchApprovedClasses = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/teacher-hours/${teacherId}/approved`, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {
        period: `${selectedYear}-${selectedMonth < 10 ? '0' + selectedMonth : selectedMonth}`
      }
    });
    if (response.data.length > 0) {
      // Check if any classes are unpaid - only set as approved if all are paid
      const hasUnpaidClasses = response.data.some(cls => cls.payout !== 'paid');
      setIsApproved(!hasUnpaidClasses);
      
      setFilteredClasses(response.data);
      const rates = {};
      const apiRemarks = {}; // Add this
      response.data.forEach(({ class_id, hourly_rate, remark }) => { // Add remark here
        rates[class_id] = hourly_rate;
        if (remark) apiRemarks[class_id] = remark; // Add this line
      });
      setHourlyRates(rates);
      setRemarks(apiRemarks); // Add this line
    } else {
      setIsApproved(false);
    }
  } catch (error) {
    console.error(t('errorFetchingApprovedClasses'), error);
  }
}, [teacherId, selectedMonth, selectedYear, API_URL, t]);

  const fetchTeacherRate = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/teacherrate/teacher/${teacherId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setTeacherRate(response.data);
    } catch (error) {
      console.error('Error fetching teacher rate:', error);
    }
  }, [teacherId, API_URL]);

  const fetchRegistrationCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/registrations/registrations-count`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const countsObject = {};
      if (Array.isArray(response.data)) {
        response.data.forEach(item => {
          countsObject[item.groupid] = item.registered_students;
        });
      }
      
      setRegistrationCounts(countsObject);
    } catch (error) {
      console.error('Error fetching registration counts:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchTeacher();
    fetchClasses();
    fetchApprovedClasses();
    fetchTeacherRate();
    fetchRegistrationCounts();

    // Initialize selected classes for unpaid classes
    if (filteredClasses.length > 0) {
      const unpaidClassIds = filteredClasses
        .filter(cls => cls.payout !== 'paid')
        .map(cls => cls.classid);
      setSelectedClasses(unpaidClassIds);
    }
  }, [fetchTeacher, fetchClasses, fetchApprovedClasses, fetchTeacherRate, fetchRegistrationCounts]);

  // Update selected classes when filtered classes change
  useEffect(() => {
    const unpaidClassIds = filteredClasses
      .filter(cls => cls.payout !== 'paid')
      .map(cls => cls.classid);
    setSelectedClasses(unpaidClassIds);
  }, [filteredClasses]);

const filterClasses = (classes, month, year) => {
  
  const filtered = classes.filter(cls => {
    const classDate = new Date(cls.date);

    const matchesMonthYear =
      classDate.getMonth() + 1 === month &&
      classDate.getFullYear() === year;

    // Change this line to use the correct field name
    const matchesBranch =
      !selectedBranch || cls.id === selectedBranch; // Use cls.id instead of cls.branch_id

    return matchesMonthYear && matchesBranch;
  });

  setFilteredClasses(filtered);
};
// Add this useEffect after your existing ones
// Add this useEffect to trigger filtering when branch changes
useEffect(() => {
  if (classes.length > 0) {
    filterClasses(classes, selectedMonth, selectedYear);
  }
}, [selectedBranch, classes, selectedMonth, selectedYear]);

  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return 'N/A';
    
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    
    let durationHours = 0;
    let durationMinutes = 0;
    
    if (duration.includes('h') || duration.includes('m')) {
      const hoursMatch = duration.match(/(\d+)h/);
      const minutesMatch = duration.match(/(\d+)m/);
      
      durationHours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
      durationMinutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
    } 
    else if (duration.includes(':')) {
      [durationHours, durationMinutes] = duration.split(':').map(Number);
    }
    
    let endHours = startHours + durationHours;
    let endMinutes = startMinutes + durationMinutes;
    
    if (endMinutes >= 60) {
      endHours += Math.floor(endMinutes / 60);
      endMinutes %= 60;
    }
    
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  const handleMonthChange = (value) => {
    setTempMonth(value);
  };

  const handleYearChange = (value) => {
    setTempYear(value);
  };

 const handleSelect = () => {
  setSelectedMonth(tempMonth);
  setSelectedYear(tempYear);
  filterClasses(classes, tempMonth, tempYear); 
};

const handleRemarkChange = (classId, value) => {
  setRemarks((prevRemarks) => ({
    ...prevRemarks,
    [classId]: value, 
  }));
};

  const convertScheduleHourToDecimal = (scheduleHour) => {
    if (!scheduleHour) return 0;
    const [hours, minutes] = scheduleHour.split('h').map(part => part.trim().replace('m', ''));
    const hoursDecimal = parseInt(hours, 10) || 0;
    const minutesDecimal = parseInt(minutes, 10) / 60 || 0;
    return hoursDecimal + minutesDecimal;
  };

  const formatDecimalToHoursMinutes = (decimalHours) => {
    const totalMinutes = Math.round(decimalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const calculateGrossTotal = () => {
    const basePay = parseFloat(totalAmount || 0);
    return basePay + otherAddition;
  };

  const calculateWithholdingTax = () => {
    if (!withholdingTax) return 0;
    const grossTotal = calculateGrossTotal();
    return grossTotal * 0.03;
  };

  const calculateNetTotal = () => {
    const grossTotal = calculateGrossTotal();
    const withholdingDeduction = calculateWithholdingTax();
    const netTotal = grossTotal - withholdingDeduction - socialSecurityDeduction - otherDeduction;
    return Math.max(0, netTotal);
  };

  // Helper function to determine approval button text
  const getApproveButtonText = () => {
    const hasUnpaidClasses = filteredClasses.some(cls => cls.payout !== 'paid');
    const hasApprovedUnpaid = filteredClasses.some(cls => cls.status === 'approved' && cls.payout !== 'paid');
    
    if (!hasUnpaidClasses) {
      return t('allClassesPaid') || 'All Classes Paid';
    } else if (hasApprovedUnpaid) {
      return t('Reapprove Selected Hours') || `Re-approve Selected Hours (${selectedClasses.length})`;
    } else {
      return t('Approve Selected Hours') || `Approve Selected Hours (${selectedClasses.length})`;
    }
  };
const isApproveDisabled = () => {
  // No unpaid classes exist
  const hasUnpaidClasses = filteredClasses.some(cls => cls.payout !== 'paid');
  if (!hasUnpaidClasses) return true;
  
  // No classes selected
  if (selectedClasses.length === 0) return true;
  
  // All selected classes are paid (shouldn't happen but extra safety)
  const selectedUnpaidClasses = filteredClasses.filter(cls => 
    selectedClasses.includes(cls.classid) && cls.payout !== 'paid'
  );
  if (selectedUnpaidClasses.length === 0) return true;
  
  return false;
};

  // Handle class selection
  const handleClassSelection = (classId, checked) => {
    setSelectedClasses(prev => {
      if (checked) {
        return [...prev, classId];
      } else {
        return prev.filter(id => id !== classId);
      }
    });
  };

  // Handle select all toggle
  const handleSelectAllToggle = () => {
    const unpaidClasses = filteredClasses.filter(cls => cls.payout !== 'paid');
    const allUnpaidSelected = unpaidClasses.every(cls => selectedClasses.includes(cls.classid));
    
    if (allUnpaidSelected) {
      // Deselect all unpaid classes
      const paidClassIds = filteredClasses
        .filter(cls => cls.payout === 'paid')
        .map(cls => cls.classid);
      setSelectedClasses(selectedClasses.filter(id => paidClassIds.includes(id)));
    } else {
      // Select all unpaid classes
      const unpaidClassIds = unpaidClasses.map(cls => cls.classid);
      setSelectedClasses([...new Set([...selectedClasses, ...unpaidClassIds])]);
    }
  };

  // True when every attendance record on the class is "To be rescheduled".
  // Such classes don't pay the teacher — they'll be paid when the rescheduled class actually runs.
  const isClassRescheduled = (cls) => {
    const records = cls?.attendance_statuses || [];
    return records.length > 0 && records.every((r) => r.status === 'To be rescheduled');
  };

  const handleApprove = () => {
    // Only process selected unpaid classes
    const selectedUnpaidClasses = filteredClasses.filter(cls =>
      selectedClasses.includes(cls.classid) && cls.payout !== 'paid'
    );
    
    if (selectedUnpaidClasses.length === 0) {
      message.warning(t('noClassesSelected') || 'Please select classes to approve');
      return;
    }

    const approvedClassesData = selectedUnpaidClasses.map(cls => {
      const studentCount = registrationCounts[cls.groupid] || 0;
      const calculatedRate = getRateBySubjectAndStudentCount(
        cls.subject_group_name, 
        studentCount, 
        teacherRate
      );
      const finalRate = hourlyRates[cls.classid] || calculatedRate;
      
      return {
        classId: cls.classid,
        hourlyRate: finalRate,
        hoursWorked: convertScheduleHourToDecimal(cls.schedule_hour),
        remark: remarks[cls.classid] || '',
      };
    });

    // Calculate total for selected classes only (skip fully-rescheduled ones)
    const selectedTotalAmount = selectedUnpaidClasses.reduce((acc, cls) => {
      if (isClassRescheduled(cls)) return acc;
      const scheduleHourDecimal = convertScheduleHourToDecimal(cls.schedule_hour);
      const studentCount = registrationCounts[cls.groupid] || 0;
      const calculatedRate = getRateBySubjectAndStudentCount(
        cls.subject_group_name,
        studentCount,
        teacherRate
      );

      const hourlyRate = hourlyRates[cls.classid] !== undefined
        ? hourlyRates[cls.classid]
        : calculatedRate;

      const classTotal = scheduleHourDecimal * hourlyRate;
      return acc + (isNaN(classTotal) ? 0 : classTotal);
    }, 0);

    const grossTotal = selectedTotalAmount + otherAddition;
    
    setConfirmData({
      teacherName: `${teacher.firstname} ${teacher.lastname}`,
      period: `${selectedMonth}/${selectedYear}`,
      totalAmount: formatAmount(grossTotal),
      approvedClasses: approvedClassesData,
      selectedCount: selectedUnpaidClasses.length,
      selectedTotalAmount: selectedTotalAmount
    });

    setShowConfirmModal(true);
  };

  const confirmApproval = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get only selected unpaid classes
      const selectedUnpaidClasses = filteredClasses.filter(cls => 
        selectedClasses.includes(cls.classid) && cls.payout !== 'paid'
      );
      
      if (selectedUnpaidClasses.length === 0) {
        message.error(t('noValidClassesSelected') || 'No valid classes selected for approval');
        return;
      }

      // Calculate totals for selected classes only (skip fully-rescheduled ones)
      const selectedBasePay = selectedUnpaidClasses.reduce((acc, cls) => {
        if (isClassRescheduled(cls)) return acc;
        const scheduleHourDecimal = convertScheduleHourToDecimal(cls.schedule_hour);
        const studentCount = registrationCounts[cls.groupid] || 0;
        const calculatedRate = getRateBySubjectAndStudentCount(
          cls.subject_group_name,
          studentCount,
          teacherRate
        );
        const finalRate = hourlyRates[cls.classid] !== undefined ? hourlyRates[cls.classid] : calculatedRate;
        const classTotal = scheduleHourDecimal * finalRate;
        return acc + (isNaN(classTotal) ? 0 : classTotal);
      }, 0);
      
      const grossTotal = selectedBasePay + otherAddition;
      const withholdingDeduction = withholdingTax ? grossTotal * 0.03 : 0;
      const netTotal = grossTotal - withholdingDeduction - socialSecurityDeduction - otherDeduction;
      
      await axios.post(`${API_URL}/teacher-hours/${teacherId}/approve`, {
        period: `${selectedYear}-${selectedMonth < 10 ? '0' + selectedMonth : selectedMonth}`,
        approvedClasses: selectedUnpaidClasses
          .filter(cls => !isClassRescheduled(cls))
          .map(cls => {
            const scheduleHourDecimal = convertScheduleHourToDecimal(cls.schedule_hour);
            const studentCount = registrationCounts[cls.groupid] || 0;
            const calculatedRate = getRateBySubjectAndStudentCount(
              cls.subject_group_name,
              studentCount,
              teacherRate
            );
            const finalRate = hourlyRates[cls.classid] !== undefined ? hourlyRates[cls.classid] : calculatedRate;

            return {
              classId: cls.classid,
              hourlyRate: finalRate,
              hoursWorked: scheduleHourDecimal.toFixed(2),
              remark: remarks[cls.classid] || ''
            };
          }),
        grossTotal: grossTotal,
        netTotal: Math.max(0, netTotal),
        withholdingTaxApplied: withholdingTax,
        withholdingTaxAmount: withholdingDeduction,
        socialSecurityDeduction: socialSecurityDeduction,
        otherDeduction: otherDeduction,
        otherDeductionNote: otherDeductionNote,
        otherAddition: otherAddition,
        otherAdditionNote: otherAdditionNote
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      message.success(t('Selected Classes Approved') || `${selectedUnpaidClasses.length} classes approved successfully`);
      setShowConfirmation(true);
      fetchApprovedClasses();
      setShowConfirmModal(false);
      
      setSocialSecurityDeduction(0);
      setOtherDeduction(0);
      setOtherDeductionNote('');
      setOtherAddition(0);
      setOtherAdditionNote('');
    } catch (error) {
      console.error(t('errorApprovingHours'), error);
      message.error(t('errorApprovingHours'));
    }
  };

  const getPlatformDisplay = (platform, t) => {
    switch (platform) {
      case 'onsite':
        return <Tag color="blue" icon={<EnvironmentOutlined />}>{t('onsite')}</Tag>;
      case 'online':
        return <Tag color="green" icon={<VideoCameraOutlined />}>{t('online')}</Tag>;
      case 'athome':
        return <Tag color="orange" icon={<HomeOutlined />}>{t('athome')}</Tag>;
      default:
        return <Tag color="default" icon={<QuestionCircleOutlined />}>-</Tag>;
    }
  };

  const handleExportCSV = () => {
    const csvData = filteredClasses.map(cls => {
      const studentCount = registrationCounts[cls.groupid] || 0;
      const calculatedRate = getRateBySubjectAndStudentCount(
        cls.subject_group_name, 
        studentCount, 
        teacherRate
      );
      const rescheduled = isClassRescheduled(cls);
      const hourlyRate = rescheduled ? 0 : (hourlyRates[cls.classid] || calculatedRate);
      const scheduleHourDecimal = convertScheduleHourToDecimal(cls.schedule_hour);

      let classSizeCategory = '';
      if (studentCount >= 1 && studentCount <= 2) {
        classSizeCategory = 'Private (1-2)';
      } else if (studentCount >= 3 && studentCount <= 5) {
        classSizeCategory = 'Small Group (3-5)';
      } else if (studentCount >= 6 && studentCount <= 10) {
        classSizeCategory = 'Large Group (6-10)';
      } else if (studentCount > 10) {
        classSizeCategory = 'Oversized (>10)';
      }
      
      return {
        'Branch': cls.branch_name,
        'Course': cls.class_code,
        'Class ID': cls.classid,
        'Subject Group': cls.subject_group_name,
        'Level': cls.level,
        'Subject Name': cls.subject_name,
        'Type': cls.isprivate ? 'Private' : 'Group',
        'Platform': cls.platform,
        'Date': formatDate(cls.date),
        'Start Time': cls.schedule_time?.slice(0, 5),
        'End Time': calculateEndTime(cls.schedule_time?.slice(0, 5), cls.schedule_hour),
        'Schedule Hours': cls.schedule_hour,
        'Registered Students': studentCount,
        'Class Size Category': classSizeCategory,
        'Hourly Rate': rescheduled ? 0 : hourlyRate,
        'Total Amount': rescheduled ? 0 : formatAmount(scheduleHourDecimal * hourlyRate),
        'Attendance': (cls.attendance_statuses || []).map(a => `${a.student_id}:${a.status}`).join(' | '),
        'Confirm Teach Status': cls.status === 'approved' ? 'Hours Approved' : 'Confirm Pending',
        'Admin Approval': cls.adminapprove === 'adminconfirm' ? 'Admin Confirmed' : 'Confirm Pending',
        'Payout Status': cls.payout === 'pending' ? 'Pending' : 'Paid',
        'Remark': remarks[cls.classid] || ''
      };
    });
    return csvData;
  };

  const getRateBySubjectAndStudentCount = (subjectGroupName, studentCount, teacherRate) => {
    if (!teacherRate || !Array.isArray(teacherRate)) return 0;
    
    let classSizeCategory;
    if (studentCount >= 1 && studentCount <= 2) {
      classSizeCategory = 'private_1_2';
    } else if (studentCount >= 3 && studentCount <= 5) {
      classSizeCategory = 'group_3_5';
    } else if (studentCount >= 6 && studentCount <= 10) {
      classSizeCategory = 'group_6_10';
    } else {
      classSizeCategory = 'private_1_2';
    }
    
    const matchingRate = teacherRate.find(rate => 
      rate.subject_group_name === subjectGroupName && 
      rate.class_size_category === classSizeCategory
    );
    
    return matchingRate ? matchingRate.rate : 0;
  };

  const handleRateChange = (classId, value) => {
    setHourlyRates({
      ...hourlyRates,
      [classId]: parseFloat(value) || 0
    });
  };

  // Get classes to display based on filter
  const getDisplayClasses = () => {
    if (showOnlyUnpaid) {
      return filteredClasses.filter(cls => cls.payout !== 'paid');
    }
    return filteredClasses;
  };

  const displayClasses = getDisplayClasses();
  
  // Calculate totals for selected classes only
  const selectedUnpaidClasses = filteredClasses.filter(cls => 
    selectedClasses.includes(cls.classid) && cls.payout !== 'paid'
  );
  
  const totalScheduleHours = selectedUnpaidClasses
    .filter(cls => !isClassRescheduled(cls))
    .reduce((acc, cls) => acc + convertScheduleHourToDecimal(cls.schedule_hour), 0);

  const totalAmount = selectedUnpaidClasses.reduce((acc, cls) => {
    if (isClassRescheduled(cls)) return acc;
    const scheduleHourDecimal = convertScheduleHourToDecimal(cls.schedule_hour);
    const studentCount = registrationCounts[cls.groupid] || 0;
    const calculatedRate = getRateBySubjectAndStudentCount(
      cls.subject_group_name,
      studentCount,
    teacherRate
    );

    const hourlyRate = hourlyRates[cls.classid] !== undefined
      ? hourlyRates[cls.classid]
      : calculatedRate;
    
    const classTotal = scheduleHourDecimal * hourlyRate;
    return acc + (isNaN(classTotal) ? 0 : classTotal);
  }, 0);

  const formatAmount = (amount) => {
    return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
 const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta", 
};
  const columns = [
    {
      title: (
        <Checkbox
          indeterminate={
            selectedClasses.length > 0 && 
            selectedClasses.length < filteredClasses.filter(cls => cls.payout !== 'paid').length
          }
          onChange={handleSelectAllToggle}
          checked={
            filteredClasses.filter(cls => cls.payout !== 'paid').length > 0 &&
            filteredClasses.filter(cls => cls.payout !== 'paid').every(cls => selectedClasses.includes(cls.classid))
          }
        >
          {t('select') || 'Select'}
        </Checkbox>
      ),
      key: 'select',
      width: 80,
      render: (_, record) => {
        const isSelectable = record.payout !== 'paid';
        return (
          <Checkbox
            checked={selectedClasses.includes(record.classid)}
            onChange={(e) => handleClassSelection(record.classid, e.target.checked)}
            disabled={!isSelectable}
          />
        );
      },
    },
    {
        title: t("branch"),
        key: "branch_name",
        dataIndex: "branch_name",
        width: 120,
        render: (_, record) => {
          const color = branchColors[record.branch_id] || "default";
          return (
            <Tag color={color}>
              {record.branch_name || t("N/A")}
            </Tag>
          );
        },
      },
    {
      title: t('group'),
      dataIndex: 'subject_group_name',
      key: 'subject_group_name',
      width: 80,
      render: (subject_group_name) => (
        <Tag color="blue" size="small">
          {subject_group_name}
        </Tag>
      ),
    },
    {
      title: t('class'),
      key: 'class_summary',
      width: 320,
      render: (_, record) => (
        <div style={{ whiteSpace: 'normal', overflowWrap: 'break-word', lineHeight: 1.35 }}>
          <div style={{ fontSize: 11, color: '#888' }}>#{record.classid}</div>
          <div style={{ fontWeight: 600 }}>{record.class_code}</div>
          <div style={{ fontSize: 12, color: '#555' }}>{record.subject_name}</div>
        </div>
      ),
    },
    {
      title: t('level'),
      dataIndex: 'level',
      key: 'level',
      width: 80,
    },
    {
      title: t('type'),
      key: 'type',
      width: 90,
      render: (_, record) => (
        record.isprivate ? (
          <Tag color="blue" icon={<UserOutlined />}>
            {t('private')}
          </Tag>
        ) : (
          <Tag color="green" icon={<TeamOutlined />}>
            {t('group')}
          </Tag>
        )
      ),
    },
    {
      title: t('platform'),
      key: 'platform',
      dataIndex: 'platform',
      width: 120,
      render: (platform) => getPlatformDisplay(platform, t),
    },
    {
      title: t('schedule') || 'Schedule',
      key: 'schedule',
      width: 160,
      render: (_, record) => {
        const start = record.schedule_time?.slice(0, 5);
        const end = calculateEndTime(start, record.schedule_hour);
        return (
          <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35 }}>
            <div style={{ fontWeight: 600 }}>{formatDate(record.date)}</div>
            <div style={{ fontSize: 12, color: '#555' }}>{start} - {end}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{record.schedule_hour}</div>
          </div>
        );
      },
    },
    {
      title: t('registeredStudents'),
      key: 'registered_students',
      width: 120,
      render: (_, record) => {
        const count = registrationCounts[record.groupid] || 0;

        let color = 'default';
        let categoryText = '';
        
        if (count >= 1 && count <= 2) {
          color = 'purple';
          categoryText = 'Private';
        } else if (count >= 3 && count <= 5) {
          color = 'blue';
          categoryText = 'Small Group';
        } else if (count >= 6 && count <= 10) {
          color = 'green';
          categoryText = 'Large Group';
        } else if (count > 10) {
          color = 'red';
          categoryText = 'Oversized';
        }
        
        return (
          <div>
            <Tag color={color}>
              {count}
            </Tag>
            {categoryText && (
              <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                {categoryText}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('attendance') || 'Attendance',
      key: 'attendance',
      width: 160,
      render: (_, record) => {
        const records = record.attendance_statuses || [];
        if (records.length === 0) {
          return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        }
        const counts = records.reduce((acc, r) => {
          acc[r.status] = (acc[r.status] || 0) + 1;
          return acc;
        }, {});
        const badge = (label, count, bg, color, border) => count > 0 && (
          <span
            key={label}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              height: 18, padding: '0 6px', marginRight: 3, marginBottom: 3,
              borderRadius: 9, background: bg, color,
              border: border ? `1px solid ${border}` : 'none',
              fontSize: 10, fontWeight: 700, lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {label}{count > 1 ? ` ${count}` : ''}
          </span>
        );
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {badge('Present', counts['Present'] || 0, '#52c41a', '#fff')}
            {badge('Absent', counts['Absent'] || 0, '#fff', '#cf1322', '#ff4d4f')}
            {badge('TBR', counts['To be rescheduled'] || 0, '#ff4d4f', '#fff')}
            {badge('Late', counts['Late'] || 0, '#faad14', '#fff')}
          </div>
        );
      },
    },
    {
      title: t('rate'),
      key: 'rate',
      width: 100,
      render: (_, record) => {
        const studentCount = registrationCounts[record.groupid] || 0;
        const calculatedRate = getRateBySubjectAndStudentCount(
          record.subject_group_name,
          studentCount,
          teacherRate
        );

        // Allow editing if class is not paid
        const isEditable = record.payout !== 'paid';

        // If the class is being rescheduled, the teacher isn't paid for it now.
        const records = record.attendance_statuses || [];
        const isRescheduled = records.length > 0 && records.every((r) => r.status === 'To be rescheduled');

        if (isRescheduled) {
          return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        }

        return (
          <InputNumber
            min={0}
            value={hourlyRates[record.classid] !== undefined ? hourlyRates[record.classid] : calculatedRate}
            onChange={(value) => handleRateChange(record.classid, value === null ? 0 : value)}
            disabled={!isEditable}
            style={{
              width: '100%',
              backgroundColor: isEditable ? '#fff' : '#f5f5f5'
            }}
            controls={false}
            formatter={value => value === 0 ? '฿ 0' : `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={value => {
              const parsed = value.replace(/฿\s?|(,*)/g, '');
              return parsed === '' ? 0 : parseFloat(parsed);
            }}
            placeholder="0"
          />
        );
      },
    },
    {
      title: t('total'),
      key: 'total',
      width: 100,
      render: (_, record) => {
        const scheduleHourDecimal = convertScheduleHourToDecimal(record.schedule_hour);
        const studentCount = registrationCounts[record.groupid] || 0;
        const calculatedRate = getRateBySubjectAndStudentCount(
          record.subject_group_name,
          studentCount,
          teacherRate
        );

        const records = record.attendance_statuses || [];
        const isRescheduled = records.length > 0 && records.every((r) => r.status === 'To be rescheduled');
        if (isRescheduled) {
          return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        }
        
        const hourlyRate = hourlyRates[record.classid] !== undefined 
          ? hourlyRates[record.classid] 
          : calculatedRate;
        
        const totalAmount = scheduleHourDecimal * hourlyRate;
        
        if (isNaN(totalAmount)) return 'N/A';
        if (totalAmount === 0) return '฿0';
        
        return `฿${formatAmount(totalAmount)}`;
      },
    },
    {
      title: t('Teacher CF'),
      key: 'status',
      width: 140,
      render: (_, record) => (
        <Tag color={record.status === 'approved' ? 'green' : 'orange'}>
          {record.status === 'approved' ? t('Teacher CF') : t('pending')}
        </Tag>
      ),
    },
    {
      title: t('Admin CF'),
      key: 'adminapprove',
      width: 140,
      render: (_, record) => (
        <Tag color={record.adminapprove === 'adminconfirm' ? 'green' : 'orange'}>
          {record.adminapprove === 'adminconfirm' ? t('Admin CF') : t('pending')}
        </Tag>
      ),
    },
    {
      title: t('Payment Status'),
      key: 'payout',
      width: 130,
      render: (_, record) => {
        const isPaid = record.payout === 'paid';
        const isApproved = record.status === 'approved';
        
        return (
          <div>
            <Tag color={isPaid ? 'green' : 'orange'}>
              {isPaid ? t('Paid') : t('Pending')}
            </Tag>
            {!isPaid && isApproved && (
              <div style={{ fontSize: '10px', color: '#faad14', marginTop: '2px' }}>
                {t('canReapprove') || 'Can Re-approve'}
              </div>
            )}
          </div>
        );
      },
    },
 {
  title: t('remark'),
  key: 'remark',
  width: 200,
  render: (_, record) => {
    const isEditable = record.payout !== 'paid';
    
    return (
      <Input
        placeholder=""
        value={remarks[record.classid] || ""}
        onChange={(e) => handleRemarkChange(record.classid, e.target.value)}
        disabled={!isEditable}
        style={{ 
          backgroundColor: isEditable ? '#fff' : '#f5f5f5'
        }}
      />
    );
  },
},
  ];

  const summaryContent = () => (
    <Table.Summary fixed>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={10}>
          <Text strong>{t('total')}</Text>
        </Table.Summary.Cell>

        <Table.Summary.Cell index={10}>
          <Text strong>{formatDecimalToHoursMinutes(totalScheduleHours)}</Text>
        </Table.Summary.Cell>

        <Table.Summary.Cell index={11} colSpan={2}></Table.Summary.Cell>

        <Table.Summary.Cell index={13}>
          <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
            ฿{formatAmount(totalAmount)}
          </Title>
        </Table.Summary.Cell>

        <Table.Summary.Cell index={14} colSpan={6}></Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );

  // Render filter toggle for unpaid classes
  const renderFilterToggle = () => {
    const unpaidCount = filteredClasses.filter(cls => cls.payout !== 'paid').length;
    const totalCount = filteredClasses.length;
    const selectedCount = selectedClasses.length;
    
    if (unpaidCount === 0) return null;
    
    return (
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Button
          type={showOnlyUnpaid ? 'primary' : 'default'}
          onClick={() => setShowOnlyUnpaid(!showOnlyUnpaid)}
        >
          {showOnlyUnpaid 
            ? (t('Show All Classes') || `Show All Classes (${totalCount})`) 
            : (t('Show Unpaid Only') || `Show Unpaid Only (${unpaidCount})`)
          }
        </Button>
        
        {selectedCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tag color="blue">
              {selectedCount} {t('Classes Selected') || 'classes selected'}
            </Tag>
            <Button 
              size="small" 
              onClick={() => setSelectedClasses([])}
              type="link"
              style={{ padding: 0, height: 'auto' }}
            >
              {t('Clear Selection') || 'Clear'}
            </Button>
          </div>
        )}
      </div>
    );
  };

// Render approve button with appropriate state
const renderApproveButton = () => {
  const hasUnpaidClasses = filteredClasses.some(cls => cls.payout !== 'paid');
  const selectedUnpaidCount = filteredClasses.filter(cls => 
    selectedClasses.includes(cls.classid) && cls.payout !== 'paid'
  ).length;
  
  if (!hasUnpaidClasses) {
    return (
      <div style={{ marginTop: '20px', textAlign: 'left' }}>
        <Button 
          type="default" 
          size="large"
          disabled
          style={{ backgroundColor: '#f6ffed', borderColor: '#52c41a', color: '#52c41a' }}
        >
          {t('All Classes Paid') || 'All Classes Paid'}
        </Button>
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
          {t('All classes have been paid. No further approval needed.')}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '20px', textAlign: 'left' }}>
      <Button 
        type="primary" 
        size="large"
        onClick={handleApprove}
        disabled={isApproveDisabled()} // <-- This is the key line
      >
        {getApproveButtonText()}
      </Button>
      <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
        {selectedUnpaidCount === 0 
          ? (t('Select Classes To Approve') || 'Select unpaid classes to approve')
          : (t('Unpaid Classes Can Be Reapproved') || `${selectedUnpaidCount} unpaid classes ready for approval`)
        }
      </div>
    </div>
  );
};

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="teacher-details-container">
        <Card title="Teacher Details" className="teacher-details-card" loading={loading}>
          
          {teacher && (
            <>
              <Card 
                title={
                  <Space align="center">
                    <UserOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                    <Title level={3} style={{ margin: 0 }}>
                      {teacher.nickname ? `(${teacher.nickname}) ` : ''}{teacher.firstname} {teacher.lastname}
                    </Title>
                  </Space>
                }
                className="teacher-info-card"
                style={{ marginBottom: '24px' }}
              >              
                <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
                  <div style={{ flex: 1, minWidth: '350px' }}>
                    {teacherRate && Array.isArray(teacherRate) && teacherRate.length > 0 ? (
                      <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 1fr',
                        gap: '4px',
                        fontSize: '12px',
                        border: '1px solid #f0f0f0',
                        borderRadius: '6px',
                        overflow: 'hidden'
                      }}>
                        {/* Header */}
                        <div style={{ 
                          padding: '6px 8px', 
                          backgroundColor: '#fafafa', 
                          fontWeight: 'bold',
                          fontSize: '10px',
                          color: '#8c8c8c'
                        }}>
                          SUBJECT
                        </div>
                        <div style={{ 
                          padding: '6px 8px', 
                          backgroundColor: '#fafafa', 
                          fontWeight: 'bold',
                          fontSize: '10px',
                          color: '#8c8c8c',
                          textAlign: 'center'
                        }}>
                          1-2
                        </div>
                        <div style={{ 
                          padding: '6px 8px', 
                          backgroundColor: '#fafafa', 
                          fontWeight: 'bold',
                          fontSize: '10px',
                          color: '#8c8c8c',
                          textAlign: 'center'
                        }}>
                          3-5
                        </div>
                        <div style={{ 
                          padding: '6px 8px', 
                          backgroundColor: '#fafafa', 
                          fontWeight: 'bold',
                          fontSize: '10px',
                          color: '#8c8c8c',
                          textAlign: 'center'
                        }}>
                          6-10
                        </div>
                        
                        {/* Data Rows */}
                        {Object.entries(
                          teacherRate.reduce((acc, rate) => {
                            const groupName = rate.subject_group_name || 'General';
                            if (!acc[groupName]) {
                              acc[groupName] = { rates: {}, subject_id: rate.subject_id };
                            }
                            acc[groupName].rates[rate.class_size_category] = rate.rate;
                            return acc;
                          }, {})
                        )
                        .sort(([, a], [, b]) => a.subject_id - b.subject_id)
                        .map(([subjectGroup, { rates }], rowIndex) => (
                          <>
                            <div key={`subject-${rowIndex}`} style={{
                              padding: '6px 8px',
                              backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#fafafa',
                              fontSize: '11px',
                              fontWeight: '500',
                              color: '#1890ff',
                              borderRight: '1px solid #f0f0f0',
                              wordWrap: 'break-word',
                              lineHeight: '1.2'
                            }}>
                              {subjectGroup}
                            </div>
                            
                            {['private_1_2', 'group_3_5', 'group_6_10'].map((category, colIndex) => (
                              <div key={`${rowIndex}-${category}`} style={{
                                padding: '6px 8px',
                                backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#fafafa',
                                textAlign: 'center',
                                borderRight: colIndex < 2 ? '1px solid #f0f0f0' : 'none'
                              }}>
                                {rates[category] ? (
                                  <span style={{ 
                                    fontSize: '12px', 
                                    fontWeight: '600',
                                    color: '#52c41a' 
                                  }}>
                                    ฿{rates[category]}
                                  </span>
                                ) : (
                                  <span style={{ color: '#d9d9d9' }}>-</span>
                                )}
                              </div>
                            ))}
                          </>
                        ))}
                      </div>
                    ) : (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '20px',
                        border: '1px dashed #d9d9d9',
                        borderRadius: '6px',
                        backgroundColor: '#fafafa'
                      }}>
                        <ExclamationCircleOutlined style={{ fontSize: '20px', color: '#d9d9d9', marginBottom: '4px' }} />
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>{t('noRatesSet')}</Text>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
                            
              <div style={{ marginBottom: '20px' }}>
      <Select
  placeholder={t("Select Branch")}
  style={{ width: 200 }}
  value={selectedBranch}
  onChange={(value) => setSelectedBranch(value)}
  allowClear
  showSearch
  optionFilterProp="children"
>
  {branches.map((branch) => (
    <Option key={branch.id} value={branch.id}>
      <Tag color={branchColors[branch.id] || "default"}>{branch.name}</Tag>
    </Option>
  ))}
</Select>


                <Space size="middle">
                  <Select
                    value={tempMonth}
                    onChange={handleMonthChange}
                    style={{ width: 120 }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <Option key={month} value={month}>
                        {new Date(0, month - 1).toLocaleString('default', { month: 'long' })}
                      </Option>
                    ))}
                  </Select>
                  
                  <Select
                    value={tempYear}
                    onChange={handleYearChange}
                    style={{ width: 100 }}
                  >
                    {Array.from({ length: 10 }, (_, i) => 2024 + i).map(year => (
                      <Option key={year} value={year}>
                        {year}
                      </Option>
                    ))}
                  </Select>
                  
                  <Button 
                    type="primary" 
                    icon={<SearchOutlined />} 
                    onClick={handleSelect}
                  >
                    {t('Search')}
                  </Button>
                  
                  <Tooltip title={t('exportToCSV')}>
                    <CSVLink
                      data={handleExportCSV()}
                      filename={`approved_hours_${teacherId}_${selectedMonth}_${selectedYear}.csv`}
                    >
                      <Button 
                        icon={<FileExcelOutlined />}
                      >
                        {t('exportToCSV')}
                      </Button>
                    </CSVLink>
                  </Tooltip>
                </Space>
              </div>

              {/* Filter Toggle for Unpaid Classes */}
              {renderFilterToggle()}
              
              {/* Approve Button */}
              {renderApproveButton()}
                          
              <Table
                columns={columns}
                dataSource={displayClasses}
                rowKey="classid"
                scroll={{ x: 'max-content' }}
                style={{ overflowX: 'auto', marginTop: '20px' }}
                pagination={false}
                size="middle"
                bordered
                summary={summaryContent}
                onRow={(record) => {
                  const records = record.attendance_statuses || [];
                  // Worst signal wins: Absent > To be rescheduled > Present
                  let bg = null;
                  if (records.some((r) => r.status === 'Absent')) {
                    bg = '#fff1f0'; // light red
                  } else if (records.some((r) => r.status === 'To be rescheduled')) {
                    bg = '#fffbe6'; // light yellow
                  } else if (records.some((r) => r.status === 'Present')) {
                    bg = '#f6ffed'; // light green
                  }
                  return bg ? { style: { backgroundColor: bg } } : {};
                }}
              />
              
              {showConfirmation && (
                <Alert
                  message={t('teacherHoursApprovalSaved')}
                  type="success"
                  showIcon
                  closable
                  onClose={() => setShowConfirmation(false)}
                  style={{ marginTop: '20px' }}
                />
              )}
            </>
          )}
        </Card>
      </div>

      <Modal
        title={
          <div>
            <Title level={4}>{t('confirm_payment')}</Title>
            {filteredClasses.some(cls => cls.status === 'approved' && cls.payout !== 'paid') && (
              <Alert
                message={t('Re-approval Notice') || 'Re-approval Notice'}
                description={t('Some classes were previously approved but not yet paid. This will update their approval with new rates and deductions.')}
                type="warning"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}
          </div>
        }
        open={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        width={700}
        footer={[
          <Button key="cancel" onClick={() => setShowConfirmModal(false)}>
            {t('cancel')}
          </Button>,
          <Button key="confirm" type="primary" onClick={confirmApproval}>
            {filteredClasses.some(cls => cls.status === 'approved' && cls.payout !== 'paid') 
              ? (t('Re-confirm') || 'Re-confirm') 
              : (t('confirm') || 'Confirm')
            }
          </Button>
        ]}
      >
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('teacherName')}>{confirmData.teacherName}</Descriptions.Item>
          <Descriptions.Item label={t('period')}>{confirmData.period}</Descriptions.Item>
          <Descriptions.Item label={t('selectedClasses')}>
            {confirmData.selectedCount || selectedClasses.length} {t('classes') || 'classes'}
          </Descriptions.Item>
          <Descriptions.Item label={t('basePay')}>
            {formatAmount(confirmData.selectedTotalAmount || totalAmount)}
          </Descriptions.Item>
        </Descriptions>
        
        <div style={{ marginTop: '16px' }}>
          <Title level={5}>{t('Additions')}</Title>
          
          <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text>{t('Other Addition Pay')}: </Text>
            <InputNumber
              value={otherAddition}
              onChange={setOtherAddition}
              min={0}
              formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/฿\s?|(,*)/g, '')}
              style={{ width: '120px' }}
            />
            <Input
              placeholder={t('note')}
              value={otherAdditionNote}
              onChange={(e) => setOtherAdditionNote(e.target.value)}
              style={{ width: '200px' }}
            />
          </div>
          
          <div style={{ 
            padding: '8px 12px', 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f',
            borderRadius: '6px',
            marginBottom: '16px'
          }}>
            <Text strong>{t('Gross Total')}: {formatAmount(calculateGrossTotal())}</Text>
          </div>
          
          <Title level={5}>{t('Deductions')}</Title>
          
          <div style={{ marginBottom: '12px' }}>
            <Checkbox 
              checked={withholdingTax} 
              onChange={() => setWithholdingTax(!withholdingTax)}
            >
              {t('Withholding Tax')} (3%)
              {withholdingTax && (
                <Text type="secondary" style={{ marginLeft: '8px' }}>
                  - {formatAmount(calculateWithholdingTax())}
                </Text>
              )}
            </Checkbox>
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <Text>{t('Social Security Insurance')}: </Text>
            <InputNumber
              value={socialSecurityDeduction}
              onChange={setSocialSecurityDeduction}
              min={0}
              formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/฿\s?|(,*)/g, '')}
              style={{ width: '150px', marginLeft: '8px' }}
            />
          </div>
          
          <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text>{t('Other Deduction')}: </Text>
            <InputNumber
              value={otherDeduction}
              onChange={setOtherDeduction}
              min={0}
              formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/฿\s?|(,*)/g, '')}
              style={{ width: '120px' }}
            />
            <Input
              placeholder={t('note')}
              value={otherDeductionNote}
              onChange={(e) => setOtherDeductionNote(e.target.value)}
              style={{ width: '200px' }}
            />
          </div>
          
          <div style={{ 
            marginTop: '20px', 
            padding: '16px', 
            backgroundColor: '#f0f8ff', 
            border: '2px solid #1890ff',
            borderRadius: '8px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                {t('Net Total')}: 
              </Title>
              <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                {formatAmount(calculateNetTotal())}
              </Title>
            </div>
            
            <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
              <div>{t('Base Pay')}: {formatAmount(totalAmount)}</div>
              {otherAddition > 0 && <div>+ {t('Additions')}: {formatAmount(otherAddition)}</div>}
              <div style={{ borderTop: '1px solid #ddd', paddingTop: '4px', fontWeight: 'bold' }}>
                = {t('Gross Total')}: {formatAmount(calculateGrossTotal())}
              </div>
              {withholdingTax && <div>- {t('W/H')}: {formatAmount(calculateWithholdingTax())}</div>}
              {socialSecurityDeduction > 0 && <div>- {t('Social Security')}: {formatAmount(socialSecurityDeduction)}</div>}
              {otherDeduction > 0 && <div>- {t('Other Deduction')}: {formatAmount(otherDeduction)}</div>}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default withRoleAccess(TeacherDetailsPage, ['superadmin']);